import './assets/base.css'
import { de as primeVueLocale } from '@/config/primevue-local.json'

import AwaitAsyncTasksService from '@/services/AwaitAsyncTasksService'
import ToastService from 'primevue/toastservice'
import { createPinia, type Pinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import PrimeVue from 'primevue/config'
//enable sentry
import { createApp } from 'vue'
import App from './App.vue'
//import 'primevue/resources/themes/lara-light-cyan/theme.css'
import VueBlocksTree from 'vue3-blocks-tree'
import 'vue3-blocks-tree/dist/vue3-blocks-tree.css'
import ConfirmationService from 'primevue/confirmationservice'
import setupAxios from '@/services/interceptorSetupService'
import { localize, setLocale } from '@vee-validate/i18n'
import { configure } from 'vee-validate'
import veeValidateDe from '@vee-validate/i18n/dist/locale/de.json'
import TitropComponentPlugin from '@/plugins/titropComponentPlugin.ts'
import storeCachePlugin from '@/plugins/storeCachePlugin.ts'
import Tooltip from 'primevue/tooltip'
import Ripple from 'primevue/ripple'
import { type ErrorMapCtx, util, z, type ZodErrorMap, ZodIssueCode, type ZodIssueOptionalMessage, ZodParsedType } from 'zod'
import type { ComposerTranslation } from 'vue-i18n'
import { useLogger } from '@/composables/useLogger.ts'
import { useMercure } from '@/composables/useMercure.ts'
import { type I18nType, setupI18n } from '@/helpers/i18n'
import { piniaClearStorePlugin } from '@/helpers/piniaClearStorePlugin'
import { Router } from '@/helpers/router'
import ganttastic from '@infectoone/vue-ganttastic'
import { syncfusionSetup } from '@/plugins/sfSetup.ts'
import DialogService from 'primevue/dialogservice'
import FormatterPlugin from '@/plugins/formatterPlugin.ts'
import { InAppNotificationService } from '@/services/InAppNotificationService'
import VueDOMPurifyHTML from 'vue-dompurify-html'
import ThemeTitropV2Sky from '@/assets/themes/titrop-v2-sky'
import ThemeTitropV2Emerald from '@/assets/themes/titrop-v2-emerald'
import ToggleButton from 'primevue/togglebutton'
import { ToggleSwitch } from 'primevue'
import IconPlugin from '@/plugins/IconPlugin.ts'
import ErrorDialogPlugin from '@/plugins/ErrorDialogPlugin.ts'
import TitropSentryPlugin from '@/plugins/TitropSentryPlugin.ts'
import PwaPlugin from '@/plugins/PwaPlugin.ts'
import VersionCheckPlugin from '@/plugins/VersionCheckPlugin.ts'
import HotkeyPlugin from '@/plugins/HotkeyPlugin.ts'
import { useTitropEvents } from '@/composables/useTitropEvents.ts'
import ActivityTrackerPlugin from '@/plugins/ActivityTrackerPlugin.ts'
import FilterServiceRules from '@/plugins/FilterServiceRules.ts'
import KeyFilter from 'primevue/keyfilter'
import { setupPushHandler } from '@/services/PushHandler.ts'
import titropHtml from './directives/titropHtml'
import ServerInfoPlugin from '@/plugins/ServerInfoPlugin.ts'
import FeatureFlagsPlugin from '@/plugins/FeatureFlagsPlugin.ts'

const themes = [ThemeTitropV2Emerald, ThemeTitropV2Sky]
const appLogger = useLogger('app') //required for early-loading useLogger
appLogger.debug('Application loaded')

try {
  window.localStorage.removeItem('vueuse-color-scheme')
  document.documentElement.classList.remove('dark')
  document.documentElement.classList.add('light')
} catch {
  // Keep startup working when storage access is unavailable.
}

setupAxios()

const i18n: I18nType = await setupI18n()

const zodErrorMap: ZodErrorMap = (issue: ZodIssueOptionalMessage, _ctx: ErrorMapCtx) => {
  const t: ComposerTranslation = i18n.global.t
  let message: string
  switch (issue.code) {
    case ZodIssueCode.invalid_type:
      if (issue.received === ZodParsedType.undefined) {
        message = t('validation.undefined.required')
      } else {
        message = t(`validation.undefined.unexpected`, {
          issue_expected: issue.expected,
          issue_received: issue.received
        })
      }
      break
    case ZodIssueCode.invalid_literal:
      message = t('validation.invalid_literal', {
        json: JSON.stringify(issue.expected, util.jsonStringifyReplacer)
      })
      break
    case ZodIssueCode.unrecognized_keys:
      message = t('validation.unrecognized_keys', {
        keys: util.joinValues(issue.keys, ', ')
      })
      break
    case ZodIssueCode.invalid_union:
      message = t('validation.invalid_union')
      break
    case ZodIssueCode.invalid_union_discriminator:
      message = t('validation.invalid_union_discriminator', {
        options: util.joinValues(issue.options)
      })
      break
    case ZodIssueCode.invalid_enum_value:
      message = t('validation.invalid_enum_value', {
        options: util.joinValues(issue.options),
        received: issue.received
      })
      break
    case ZodIssueCode.invalid_arguments:
      message = t('validation.invalid_arguments')
      break
    case ZodIssueCode.invalid_return_type:
      message = t('validation.invalid_return_type')
      break
    case ZodIssueCode.invalid_date:
      message = t('validation.invalid_date')
      break
    case ZodIssueCode.invalid_string:
      if (typeof issue.validation === 'object') {
        if ('includes' in issue.validation) {
          message = t('validation.invalid_string.object_must_include', { issue_validation_includes: issue.validation.includes })
          if (typeof issue.validation.position === 'number') {
            message = t('validation.invalid_string.object_must_include_at', { issue_validation_includes: issue.validation.includes })
          }
        } else if ('startsWith' in issue.validation) {
          message = t('validation.invalid_string.starts_with', { issue_validation_startsWith: issue.validation.startsWith })
        } else if ('endsWith' in issue.validation) {
          message = t('validation.invalid_string.ends_with', { issue_validation_endsWith: issue.validation.endsWith })
        } else {
          util.assertNever(issue.validation)
        }
      } else if (issue.validation !== 'regex') {
        message = t('validation.invalid_string.regex', { issue_validation: issue.validation })
      } else {
        message = t('validation.invalid')
      }
      break
    case ZodIssueCode.too_small:
      if (issue.type === 'array') {
        if (issue.exact) message = t('validation.too_small.array_exact', { issue_minimum: issue.minimum })
        else if (issue.inclusive) message = t('validation.too_small.array_inclusive', { issue_minimum: issue.minimum })
        else message = t('validation.too_small.array_more_than', { issue_minimum: issue.minimum })
      } else if (issue.type === 'string') {
        if (issue.exact) message = t('validation.too_small.string_exact', { issue_minimum: issue.minimum })
        else if (issue.inclusive) message = t('validation.too_small.string_inclusive', { issue_minimum: issue.minimum })
        else message = t('validation.too_small.string_over', { issue_minimum: issue.minimum })
      } else if (issue.type === 'number') {
        if (issue.exact) message = t('validation.too_small.number_exact', { issue_minimum: issue.minimum })
        else if (issue.inclusive) message = t('validation.too_small.number_inclusive', { issue_minimum: issue.minimum })
        else message = t('validation.too_small.number_greater_than', { issue_minimum: issue.minimum })
      } else if (issue.type === 'date') {
        if (issue.exact) message = t('validation.too_small.date_exact', { minimum: new Date(Number(issue.minimum)) })
        else if (issue.inclusive) message = t('validation.too_small.date_inclusive', { minimum: new Date(Number(issue.minimum)) })
        else message = t('validation.too_small.date_greater_than', { minimum: new Date(Number(issue.minimum)) })
      } else message = t('validation.invalid')
      break
    case ZodIssueCode.too_big:
      if (issue.type === 'array') {
        if (issue.exact) message = t('validation.too_big.array_exact', { issue_maximum: issue.maximum })
        else if (issue.inclusive) message = t('validation.too_big.array_at_most', { issue_maximum: issue.maximum })
        else message = t('validation.too_big.array_less_than', { issue_maximum: issue.maximum })
      } else if (issue.type === 'string') {
        if (issue.exact) message = t('validation.too_big.string_exact', { issue_maximum: issue.maximum })
        else if (issue.inclusive) message = t('validation.too_big.string_at_most', { issue_maximum: issue.maximum })
        else message = t('validation.too_big.string_under', { issue_maximum: issue.maximum })
      } else if (issue.type === 'number') {
        if (issue.exact) message = t('validation.too_big.number_exact', { issue_maximum: issue.maximum })
        else if (issue.inclusive) message = t('validation.too_big.number_less_than_or_equal', { issue_maximum: issue.maximum })
        else message = t('validation.too_big.number_less_than', { issue_maximum: issue.maximum })
      } else if (issue.type === 'bigint') {
        if (issue.exact) message = t('validation.too_big.bigint_exact', { issue_maximum: issue.maximum })
        else if (issue.inclusive) message = t('validation.too_big.bigint_less_than_or_equal', { issue_maximum: issue.maximum })
        else message = t('validation.too_big.bigint_less_than', { issue_maximum: issue.maximum })
      } else if (issue.type === 'date') {
        if (issue.exact)
          message = t('validation.too_big.date_exact', {
            maximum: new Date(Number(issue.maximum))
          })
        else if (issue.inclusive)
          message = t('validation.too_big.date_smaller_than_or_equal', {
            maximum: new Date(Number(issue.maximum))
          })
        else
          message = t('validation.too_big.date_smaller_than', {
            maximum: new Date(Number(issue.maximum))
          })
      } else message = t('validation.invalid')
      break
    case ZodIssueCode.custom:
      message = t('validation.invalid')
      break
    case ZodIssueCode.invalid_intersection_types:
      message = t('validation.invalid_intersection_types')
      break
    case ZodIssueCode.not_multiple_of:
      message = t('validation.not_multiple_of', { issue_multipleOf: issue.multipleOf })
      break
    case ZodIssueCode.not_finite:
      message = t('validation.not_finite')
      break
    default:
      message = _ctx.defaultError
  }
  return { message }
}
z.setErrorMap(zodErrorMap)

useTitropEvents().onLoginSuccess.on(() => {
  void useMercure().connect()
})
useTitropEvents().onLogout.on(() => {
  useMercure().disconnect()
})

setLocale('de')
configure({ generateMessage: localize('de', veeValidateDe) })

syncfusionSetup()

if (!(window.__TITROP__ as TitropScope | undefined)) window.__TITROP__ = {} as TitropScope

const app = createApp(App)
const pinia: Pinia = createPinia()
pinia.use(piniaPluginPersistedstate)
pinia.use(piniaClearStorePlugin)

if (import.meta.env.DEV) app.config.performance = true
else {
  app.config.compilerOptions.whitespace = 'condense'
  app.config.compilerOptions.comments = false
}

app.provide('$i18n', i18n)
let modeActive = false
window.addEventListener('keydown', (event: KeyboardEvent) => {
  if (event.key == 'v' && event.altKey) {
    modeActive = !modeActive
    console.log('M: ' + (modeActive ? 'ON' : 'OFF'))
  }
})

// Workaround to catch Intlify warnings
// @see https://github.com/intlify/vue-i18n/issues/1861
const intlifyLogger = useLogger('Intlify')
const originalWarn = window.console.warn
window.console.warn = (...args: unknown[]) => {
  if (args[0] && typeof args[0] === 'string' && args[0].startsWith('[intlify]')) {
    intlifyLogger.warn(args[0].substring(10))
  } else originalWarn(...args)
}

setupPushHandler(Router)
app
    .use(i18n)
    .use(PrimeVue, {
      ripple: true,
      zIndex: {
        modal: 1005, //dialog, sidebar
        overlay: 1100, //dropdown, overlaypanel
        menu: 1005, //overlay menus
        tooltip: 1200 //tooltip,
      },
      theme: {
        preset: themes[1],
        options: {
          darkModeSelector: 'html.dark',
          cssLayer: {
            name: 'primevue',
            order: 'tailwind-base, primevue, tailwind-utilities'
          }
        }
      },
      unstyled: false,
      locale: primeVueLocale
    })
    .directive('titrop-html', titropHtml)
    .directive('tooltip', Tooltip)
    .directive('ripple', Ripple)
    .directive('keyfilter', KeyFilter)
    .component('ToggleButton', ToggleButton)
    .component('ToggleSwitch', ToggleSwitch)

    .use(storeCachePlugin)
    .use(pinia)
    .use(Router)
    .use(ErrorDialogPlugin)
    //.use(TitropSentryPlugin)
    .use(ganttastic)
    .use(IconPlugin)
    .use(TitropComponentPlugin)
    .use(FormatterPlugin(i18n.global))
    .use(VueDOMPurifyHTML, {
      default: {
        USE_PROFILES: { html: true, svg: true, svgFilters: true },
        FORBID_TAGS: ['input', 'video', 'select', 'textarea', 'form', 'map', 'audio', 'script', 'style', 'optgroup'],
        FORBID_ATTR: ['style'],
        ADD_TAGS: [],
        ADD_ATTR: [],
        ALLOW_ARIA_ATTR: true,
        ALLOW_DATA_ATTR: true
      }
    })
    .use(ToastService)
    .use(ConfirmationService)
    .use(AwaitAsyncTasksService)
    .use(DialogService)
    .use(InAppNotificationService)
    .use(VueBlocksTree, {
      treeName: 'BlocksTree',
      nodeName: 'BlockNode'
    })
    .use(VersionCheckPlugin)
    .use(PwaPlugin)
    .use(ActivityTrackerPlugin)
    .use(FilterServiceRules)
    .use(HotkeyPlugin)
    .use(ServerInfoPlugin)
    .use(FeatureFlagsPlugin, {
      loadOnInit: true,
      retryOnError: true,
      maxRetries: 3,
      silent: false
    })
// }
app.mount('#app')
export const appContext = app._context
